import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import Backend from "i18next-xhr-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { languageAnalytics } from './utils/analytics';
import { RegionalDetection } from './utils/regional-detection';

// Normalize initial language to base and seed localStorage to avoid en-US toggles
const initialBrowserLang = (typeof navigator !== "undefined" && navigator.language) ? navigator.language : "en";
const initialBaseLang = (initialBrowserLang || "en").split("-")[0];
if (typeof localStorage !== "undefined") {
  localStorage.setItem("i18nextLng", initialBaseLang);
}

// Simple language detection and regional mapping
const detectLanguageWithRegion = () => {
  // Get browser languages in order of preference
  const languages = [];
  
  // Primary: navigator.language
  if (navigator.language) {
    languages.push(navigator.language);
  }
  
  // Secondary: navigator.languages array
  if (navigator.languages && navigator.languages.length > 0) {
    languages.push(...navigator.languages);
  }
  
  // Process each language with regional detection
  for (const lang of languages) {
    const detectedLanguage = RegionalDetection.getPreferredLanguageFromLocale(lang);
    
    if (detectedLanguage && ['en', 'de', 'es', 'nl'].includes(detectedLanguage)) {
      // Track the auto-detection
      languageAnalytics.trackLanguageAutoDetected(
        detectedLanguage,
        'browser',
        lang
      );

      // Check for regional detection success
      if (detectedLanguage === 'es' && RegionalDetection.detectLatinAmericanSpanish([lang])) {
        languageAnalytics.trackRegionalDetectionSuccess(
          'es',
          lang.split('-')[1] || 'unknown',
          'Latin America'
        );
      }
      
      return detectedLanguage;
    }
  }
  
  return 'en'; // Default fallback
};

const cacheLanguage = (lng: string) => {
  if (typeof lng !== "string" || !lng) return;
  const base = lng.split('-')[0];
  if (i18n.services.languageDetector?.cacheUserLanguage) {
    i18n.services.languageDetector.cacheUserLanguage(base);
  }
  if (typeof localStorage !== "undefined") {
    localStorage.setItem("i18nextLng", base);
  }
};

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
        // Backend configuration
        backend: {
          loadPath: '/locales/{{lng}}.json',
          queryStringParams: { v: '20251229-02' },
        },
    
    // Supported languages - added Spanish and Dutch
    supportedLngs: ["en", "de", "es", "nl"],
    
    // Enhanced fallback hierarchy
    fallbackLng: {
      'es': ['es', 'en'],
      'nl': ['nl', 'en'],
      'de': ['de', 'en'],
      'default': ['en']
    },
    
    // Language detection configuration (force base code from cache)
    detection: {
      order: [
        'localStorage'
      ],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      excludeCacheFor: ['cimode'],
    },

    // Additional options
    debug: false,
    saveMissingTo: 'current',
    
    // Interpolation options
    interpolation: {
      escapeValue: false, // React already escapes values
    },
    
    // Load language synchronously on init
    initImmediate: false,
    
    // Language switching options
    load: 'languageOnly', // Load 'en' instead of 'en-US'
    
    // Cleanup language codes
    cleanCode: true,

    // Treat non-explicit languages like 'en-US' as supported base 'en'
    nonExplicitSupportedLngs: true,
    
    // Non-existing key behavior
    returnEmptyString: false,
    returnNull: false,
    
    // Suppress missing key warnings in production
    saveMissing: false,
    missingKeyHandler: import.meta.env.DEV ? undefined : () => {},
    
    // React specific options
    // Disable Suspense to avoid blank fallback flashes when translations load
    react: {
      useSuspense: false,
    }
  });

// Apply regional detection after initialization
i18n.on('initialized', () => {
  const detectedLanguage = detectLanguageWithRegion();
  const currentLanguage = i18n.language;
  const currentBase = (currentLanguage || 'en').split('-')[0];

  // Normalize regional languages immediately (e.g., en-US -> en) and cache
  if (currentLanguage && currentLanguage.includes('-')) {
    cacheLanguage(currentBase);
    i18n.changeLanguage(currentBase);
    return;
  }

  // Otherwise, apply detected base language if different and cache it
  if (detectedLanguage && detectedLanguage !== currentLanguage) {
    cacheLanguage(detectedLanguage);
    i18n.changeLanguage(detectedLanguage);
  } else {
    cacheLanguage(currentBase);
  }
});

// Track language changes (manual selections)
i18n.on('languageChanged', (lng) => {
  const base = (lng || 'en').split('-')[0];

  // Normalize regional languages immediately (e.g., en-US -> en)
  if (lng.includes('-') && base !== lng) {
    cacheLanguage(base);
    i18n.changeLanguage(base);
    return;
  }

  cacheLanguage(base);

  const storedLanguage = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
  
  // Track manual selection when differing from stored/detected value
  if (storedLanguage && base !== storedLanguage) {
    languageAnalytics.trackLanguageManuallySelected(base, storedLanguage, storedLanguage);
  }
});

export default i18n;
